import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

const DEFAULT_MESSAGE = 'You have unsaved changes. Discard them and leave this page?';

// Warn before losing unsaved user input, on both exit paths: in-app
// navigation (React Router blocker — sidebar links, breadcrumbs, ⌘K,
// back/forward) and full page unload (refresh, tab close, external link).
//
// Requires the app to be mounted under a data router (RouterProvider) —
// `useBlocker` throws under a plain <BrowserRouter>.
//
// Returns a `bypass` function: call it right before a programmatic
// navigate that follows a successful save, so the guard doesn't fire on
// the way to the freshly saved record.
export default function useUnsavedChangesGuard(isDirty, message = DEFAULT_MESSAGE) {
  const bypassRef = useRef(false);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isDirty &&
        !bypassRef.current &&
        currentLocation.pathname !== nextLocation.pathname,
      [isDirty]
    )
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(message)) blocker.proceed();
    else blocker.reset();
  }, [blocker, message]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => {
      if (bypassRef.current) return;
      event.preventDefault();
      // Chrome ignores preventDefault alone; returnValue must be set for
      // the native leave-site prompt to appear.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return useCallback(() => {
    bypassRef.current = true;
  }, []);
}
